import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CommonCardContainer } from "../../components/DashboardCards/cardStyles";
import RoundTab from "../../components/RoundTabButtons/RoundTab";
import Select from "../../components/ReactSelectDropdown";
import { filterOption } from "../../components/ReactSelectDropdown/ReactSelectDropdown";
import SingleDatePicker from "../../components/SingleDatePicker/SingleDatePicker";
import CreateProjectDropdown from "../../components/DropdownStyle/CreateProjectDropdown";
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  PageTitle,
  RedStar,
} from "../../styledComponents/common";
import { CrossIcon } from "../../styledComponents/createProject";
import {
  HorizontalLineDivider,
} from "../../styledComponents/Activity";
import {
  MonthDateBox,
  MonthDateContainer,
  WeekNumberBox,
} from "../ShiftSchedule/CreateShift/createShiftStyles";
import deleteIcon from "../../assets/img/icons/delete-3.svg";
import HelperIcon from "components/HelperIcon/HelperIcon";

const SHORT_WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const MONTH_GRID_COLUMNS = 7;
const MONTH_TOTAL_DAYS = 31;
const LAST_DAY_SPAN_COLUMNS =
  (MONTH_GRID_COLUMNS - (MONTH_TOTAL_DAYS % MONTH_GRID_COLUMNS)) %
    MONTH_GRID_COLUMNS || MONTH_GRID_COLUMNS;

const dateText = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  21: "21st",
  22: "22nd",
  23: "23rd",
  31: "31st",
  "Last Day": "last",
};

const capitalizeSummary = text =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const findDateText = date => {
  if (dateText[date]) {
    return dateText[date];
  } else {
    return `${date}th`;
  }
};

const RecurringScheduleSection = ({
  isRecurringScheduleOpen,
  setIsRecurringScheduleOpen,
  selectedRecurringSchedule,
  setSelectedRecurringSchedule,
  selectedRecurringScheduleDays,
  toggleRecurringWeekDay,
  monthlyScheduleMode,
  setMonthlyScheduleMode,
  interval,
  handleIntervalChange,
  handleMonthlyIntervalChange,
  activeMonthDates,
  onMonthDateSelect,
  activeMonthDays,
  onMonthDaySelect,
  saveErrors,
  endDate,
  onEndDateSelect,
  startDate,
  monthlyDatesSummary,
  monthlyDaysSummary,
  isRecurringDisabled,
}) => {
  const { t } = useTranslation();
  const weekDays = SHORT_WEEK_DAYS;
  const daysOfWeek = FULL_WEEK_DAYS;

  const monthlyIntervalOptions = [
    { label: "Every month", value: 1 },
    { label: "Every 2 months", value: 2 },
    { label: "Every 3 months", value: 3 },
    { label: "Every 6 months", value: 6 },
    { label: "Every 12 months", value: 12 },
  ];

  const renderRepeatIntervalInput = unit => {
    // Determine max value based on schedule type
    let maxValue;
    if (selectedRecurringSchedule === "daily") {
      maxValue = 150;
    } else if (selectedRecurringSchedule === "weekly") {
      maxValue = 25;
    } else {
      maxValue = undefined; // No max for other types
    }

    return (
      <CommonFlex
        alignItems="start"
        direction="column"
        gap="5px"
        margin="20px 0 0"
        
      >
        <CommonFlex style={{ width: "100%" }}>
          <CommonText>Repeat every</CommonText>
          <InputField
            type="number"
            min="1"
            max={maxValue}
            value={interval}
            onChange={handleIntervalChange}
            disabled={isRecurringDisabled}
            style={{
              maxWidth: "90px",
              textAlign: "center",
              border:
                saveErrors && saveErrors["interval"]
                  ? "1px solid #fe5969"
                  : undefined,
              backgroundColor:
                saveErrors && saveErrors["interval"]
                  ? "rgba(252, 87, 104, 0.05)"
                  : isRecurringDisabled
                  ? "#f5f5f5"
                  : undefined,
              cursor: isRecurringDisabled ? "not-allowed" : "text",
              opacity: isRecurringDisabled ? 0.6 : 1,
            }}
          />
          <CommonText>{unit}</CommonText>
        </CommonFlex>
        {saveErrors && saveErrors["interval"] && (
          <FormAlert style={{ marginBottom: 0 }}>
            {saveErrors["interval"]}
          </FormAlert>
        )}
      </CommonFlex>
    );
  };

  const renderMonthlyIntervalSelect = () => {
    const selectedOption =
      monthlyIntervalOptions.find(option => option.value === interval) ||
      monthlyIntervalOptions[0];

    return (
      <CommonFlex
        alignItems="center"
        justifyContent="flex-end"
        gap="10px"
        margin="0"
      >
        <Select
          value={selectedOption}
          options={monthlyIntervalOptions}
          onChange={handleMonthlyIntervalChange}
          isDisabled={isRecurringDisabled}
          styles={CreateProjectDropdown({
            error: saveErrors && saveErrors["interval"],
          })}
          filterOption={filterOption}
        />
        {saveErrors && saveErrors["interval"] && (
          <FormAlert style={{ marginBottom: 0 }}>
            {saveErrors["interval"]}
          </FormAlert>
        )}
      </CommonFlex>
    );
  };

  if (!isRecurringScheduleOpen) {
    return null;
  }

  return (
    <CommonCardContainer style={{ width: "100%", minWidth: "320px" }}>
      <CommonFlex  margin="0px 0px 10px 0px" disableResponsive={true} justifyContent="space-between" alignItems="center">
        <CommonFlex disableResponsive={true} gap="4px" alignItems="center">
        <PageTitle >Recurring Schedule</PageTitle>
        <HelperIcon text={t("recurring_job_covers_jobs_that_need_more_than_a_single_visit_it_could_span_days_weeks_or_even_months")} />
        </CommonFlex>
        {!isRecurringDisabled && (
          <CrossIcon
            src={deleteIcon}
            alt="delete"
            onClick={() => setIsRecurringScheduleOpen(false)}
          />
        )}
      </CommonFlex>

      <CommonFlex
        gap="4px"
        alignItem="center"
        style={{
          opacity: isRecurringDisabled ? 0.6 : 1,
          cursor: isRecurringDisabled ? "not-allowed" : "auto",
          pointerEvents: isRecurringDisabled ? "none" : "auto",
        }}
      >
        <RoundTab
          onClick={() => setSelectedRecurringSchedule("daily")}
          selected={selectedRecurringSchedule === "daily"}
          text="Daily"
        />
        <RoundTab
          onClick={() => setSelectedRecurringSchedule("weekly")}
          selected={selectedRecurringSchedule === "weekly"}
          text="Weekly"
        />
        <RoundTab
          onClick={() => setSelectedRecurringSchedule("monthly")}
          selected={selectedRecurringSchedule === "monthly"}
          text="Monthly"
        />
      </CommonFlex>
      {saveErrors && saveErrors["selectedRecurringSchedule"] && (
        <FormAlert>{saveErrors["selectedRecurringSchedule"]}</FormAlert>
      )}

      {/* Daily Schedule Section */}
      {selectedRecurringSchedule === "daily" &&
        renderRepeatIntervalInput("day(s)")}

      {/* Weekly Schedule Section */}
      {selectedRecurringSchedule === "weekly" && (
        <>
          <CommonGrid margin="24px 0">
            <CommonFlex
              gap="4px"
              alignItem="flex-start"
              justifyContent="flex-start"
              style={{
                opacity: isRecurringDisabled ? 0.6 : 1,
                cursor: isRecurringDisabled ? "not-allowed" : "auto",
                pointerEvents: isRecurringDisabled ? "none" : "auto",
              }}
            >
              {daysOfWeek.map(day => (
                <RoundTab
                  key={day}
                  onClick={() => toggleRecurringWeekDay(day)}
                  selected={selectedRecurringScheduleDays.includes(day)}
                  text={day.slice(0, 3)}
                  showIcon={false}
                />
              ))}
            </CommonFlex>

            {saveErrors &&
              saveErrors["selectedRecurringScheduleDay"] && (
                <FormAlert>
                  {saveErrors["selectedRecurringScheduleDay"]}
                </FormAlert>
              )}
          </CommonGrid>
          {renderRepeatIntervalInput("week(s)")}
        </>
      )}

      {/* Monthly Schedule Section */}
      {selectedRecurringSchedule === "monthly" && (
        <CommonGrid
          disableResponsive
          alignItem="center"
          justifyContent="space-between"
          columns="1fr 1fr"
          margin="24px 0 "
        >
          <CommonGrid
            gap="4px"
            alignItem="flex-start"
            justifyContent="flex-start"
            disableResponsive={true}
            style={{
              minWidth: "220px",
              opacity: isRecurringDisabled ? 0.6 : 1,
              cursor: isRecurringDisabled ? "not-allowed" : "auto",
              pointerEvents: isRecurringDisabled ? "none" : "auto",
            }}
          >
            <RoundTab
              onClick={() => setMonthlyScheduleMode("dates")}
              selected={monthlyScheduleMode === "dates"}
              text="On dates"
            />
            <RoundTab
              onClick={() => setMonthlyScheduleMode("days")}
              selected={monthlyScheduleMode === "days"}
              text="On days"
            />
          </CommonGrid>
          {renderMonthlyIntervalSelect()}
        </CommonGrid>
      )}

      {/* Monthly Schedule - On Dates Section */}
      {selectedRecurringSchedule === "monthly" &&
        monthlyScheduleMode === "dates" && (
          <>
            <CommonFlex
              margin="15px 0"
              direction="column"
              gap="10px"
              width="100%"
            >
              <CommonFlex
                bgColor="#F3F5F9"
                justifyContent="flex-start"
                width="100%"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "#F3F5F9",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    width: "100%",
                  }}
                >
                  <PageTitle
                    fontSize="15px"
                    style={{ textAlign: "center", width: "fit-content" }}
                  >
                    Dates in month
                  </PageTitle>
                  <HorizontalLineDivider margin="0 0 10px" />
                  <MonthDateContainer
                    style={{
                      opacity: isRecurringDisabled ? 0.6 : 1,
                      cursor: isRecurringDisabled ? "not-allowed" : "auto",
                      pointerEvents: isRecurringDisabled ? "none" : "auto",
                    }}
                  >
                    {[
                      ...Array.from(
                        { length: MONTH_TOTAL_DAYS },
                        (_, idx) => idx + 1,
                      ),
                      "Last Day",
                    ].map(value => {
                      const isLastDay = value === "Last Day";
                      return (
                        <MonthDateBox
                          key={value}
                          active={!!activeMonthDates[value]}
                          onClick={() => onMonthDateSelect(value)}
                          spanColumns={
                            isLastDay ? LAST_DAY_SPAN_COLUMNS : undefined
                          }
                        >
                          {isLastDay ? "Last day of Month" : value}
                        </MonthDateBox>
                      );
                    })}
                  </MonthDateContainer>
                </div>
              </CommonFlex>
              {monthlyDatesSummary && (
                <CommonFlex
                  justifyContent="flex-start"
                  margin="12px 0 0"
                  width="100%"
                >
                  <PageTitle fontSize="15px">
                    {monthlyDatesSummary}
                  </PageTitle>
                </CommonFlex>
              )}
              {saveErrors &&
                saveErrors["activeMonthDates"] !== undefined &&
                saveErrors["activeMonthDates"] && (
                  <FormAlert>{saveErrors["activeMonthDates"]}</FormAlert>
                )}
            </CommonFlex>
          </>
        )}

      {/* Monthly Schedule - On Days Section */}
      {selectedRecurringSchedule === "monthly" &&
        monthlyScheduleMode === "days" && (
          <>
            <CommonFlex
              margin="15px 0"
              direction="column"
              gap="10px"
              width="100%"
            >
              <CommonFlex justifyContent="flex-start" width="100%">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "#F3F5F9",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid #e0e0e0",
                    width: "100%",
                  }}
                >
                  <PageTitle
                    fontSize="15px"
                    style={{ textAlign: "center", width: "fit-content" }}
                  >
                    Week Days of Month
                  </PageTitle>
                  <HorizontalLineDivider margin="0 0 10px" />

                  <CommonFlex
                    gap="10px"
                    justifyContent="flex-start"
                    width="100%"
                    style={{ paddingLeft: "10px" }}
                    disableResponsive={true}
                  >
                    {/* Week Position Labels - Vertical Column */}
                    <CommonGrid disableResponsive={true} gap="10px" columns="35px">
                      <WeekNumberBox>1st</WeekNumberBox>
                      <WeekNumberBox>2nd</WeekNumberBox>
                      <WeekNumberBox>3rd</WeekNumberBox>
                      <WeekNumberBox>4th</WeekNumberBox>
                    </CommonGrid>

                    {/* Week Days Grid - 7 columns responsive */}
                    <MonthDateContainer
                      style={{
                        opacity: isRecurringDisabled ? 0.6 : 1,
                        cursor: isRecurringDisabled ? "not-allowed" : "auto",
                        pointerEvents: isRecurringDisabled ? "none" : "auto",
                        flex: 1,
                      }}
                    >
                      {Array.from(
                        { length: 28 },
                        (_, index) => index,
                      ).map(day => (
                        <MonthDateBox
                          key={day}
                          active={!!activeMonthDays[day]}
                          onClick={() => onMonthDaySelect(day)}
                        >
                          {weekDays[day % 7]}
                        </MonthDateBox>
                      ))}
                    </MonthDateContainer>
                  </CommonFlex>
                </div>
              </CommonFlex>
              {monthlyDaysSummary && (
                <CommonFlex
                  justifyContent="flex-start"
                  margin="12px 0 0"
                  width="100%"
                >
                  <PageTitle fontSize="15px">
                    {monthlyDaysSummary}
                  </PageTitle>
                </CommonFlex>
              )}
              {saveErrors &&
                saveErrors["activeMonthDays"] !== undefined &&
                saveErrors["activeMonthDays"] && (
                  <FormAlert>{saveErrors["activeMonthDays"]}</FormAlert>
                )}
            </CommonFlex>
          </>
        )}

      {/* Summary Section */}
      {(selectedRecurringSchedule === "weekly" ||
        selectedRecurringSchedule === "daily") && (
        <PageTitle style={{ margin: "15px 0 0 0" }} fontSize="15px">
          {selectedRecurringSchedule === "weekly"
            ? selectedRecurringScheduleDays.length > 0
              ? `${selectedRecurringScheduleDays.join(", ")} of each week`
              : "Select days of the week"
            : `Repeat every ${interval || 1} day(s)`}
        </PageTitle>
      )}

      {/* End Date Section */}
      <CommonGrid columns="1fr" margin="20px 0 0 0" alignItem="center">
        <InputWithLabelSection>
          <InputLabel>
            End date <RedStar>*</RedStar>
          </InputLabel>
          <SingleDatePicker
            placeholder="Select End Date"
            date={endDate ? endDate : null}
            onDateChange={onEndDateSelect}
            disablePreviousDatesFrom={startDate}
            disabled={isRecurringDisabled}
            error={
              saveErrors &&
              saveErrors["endDate"] !== undefined &&
              saveErrors["endDate"] &&
              saveErrors["endDate"]
            }
            width="100%"
          />
          {saveErrors &&
            saveErrors["endDate"] !== undefined &&
            saveErrors["endDate"] && (
              <FormAlert>{saveErrors["endDate"]}</FormAlert>
            )}
        </InputWithLabelSection>
      </CommonGrid>
    </CommonCardContainer>
  );
};

export default RecurringScheduleSection;

